import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Lock,
  KeyRound,
  Loader2,
  CheckCircle,
  AlertCircle,
  LogOut,
  ChefHat,
  Phone
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const ADMIN_WHATSAPP_SETTINGS_URL =
  'https://www.sapori1989.it/menu-digitale/api/admin/settings/whatsapp-number.php';

const normalizeWhatsappNumber = (value) => {
  if (!value) return '';
  let digits = String(value).replace(/\D+/g, '');

  if (digits.startsWith('0039')) digits = digits.slice(2);
  if (digits.startsWith('+')) digits = digits.replace('+', '');

  return digits;
};

export const AdminAccount = () => {
  const navigate = useNavigate();
  const { user, logout, requestPasswordReset, updatePassword, token } = useAuth();

  const [isRequestingReset, setIsRequestingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');

  const [restaurantWhatsapp, setRestaurantWhatsapp] = useState('');
  const [isLoadingWhatsapp, setIsLoadingWhatsapp] = useState(true);
  const [isSavingWhatsapp, setIsSavingWhatsapp] = useState(false);
  const [whatsappError, setWhatsappError] = useState('');
  const [whatsappSaved, setWhatsappSaved] = useState(false);

  useEffect(() => {
    const fetchWhatsappNumber = async () => {
      setIsLoadingWhatsapp(true);
      setWhatsappError('');
      setWhatsappSaved(false);

      try {
        const response = await fetch(ADMIN_WHATSAPP_SETTINGS_URL, {
          method: 'GET',
          headers: token
            ? {
                Authorization: `Bearer ${token}`
              }
            : {},
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Impossibile caricare il numero WhatsApp');
        }

        const data = await response.json();
        const number = data?.number || data?.whatsapp_number || '';
        setRestaurantWhatsapp(number);
      } catch (err) {
        console.error('Error fetching WhatsApp number:', err);
        setWhatsappError('Errore caricamento numero WhatsApp locale');
      } finally {
        setIsLoadingWhatsapp(false);
      }
    };

    fetchWhatsappNumber();
  }, [token]);

  const handleRequestReset = async () => {
    setIsRequestingReset(true);
    setError('');

    const result = await requestPasswordReset(user?.email);

    if (result.success) {
      setResetSent(true);
      toast.success('Email inviata! Controlla la tua casella di posta.');
    } else {
      setError(result.error || 'Errore invio email');
    }

    setIsRequestingReset(false);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    setIsUpdating(true);

    const result = await updatePassword(newPassword);

    if (result.success) {
      toast.success('Password aggiornata con successo!');
      setShowPasswordForm(false);
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setError(result.error || 'Errore aggiornamento password');
    }

    setIsUpdating(false);
  };

  const handleSaveWhatsappNumber = async (e) => {
    e.preventDefault();

    setWhatsappError('');
    setWhatsappSaved(false);

    const normalized = normalizeWhatsappNumber(restaurantWhatsapp);

    if (!normalized) {
      setWhatsappError('Inserisci un numero WhatsApp valido');
      return;
    }

    if (normalized.length < 10) {
      setWhatsappError('Il numero WhatsApp sembra troppo corto');
      return;
    }

    setIsSavingWhatsapp(true);

    try {
      const response = await fetch(ADMIN_WHATSAPP_SETTINGS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          number: normalized
        })
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || 'Errore salvataggio numero WhatsApp');
      }

      setRestaurantWhatsapp(data?.number || normalized);
      setWhatsappSaved(true);
      toast.success('Numero WhatsApp locale salvato con successo!');
    } catch (err) {
      console.error('Error saving WhatsApp number:', err);
      setWhatsappError(err?.message || 'Errore salvataggio numero WhatsApp');
    } finally {
      setIsSavingWhatsapp(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full gradient-instagram flex items-center justify-center">
              <User className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Account</h1>
              <p className="text-xs text-muted-foreground">Gestione profilo admin</p>
            </div>
          </div>

          <Button variant="outline" size="sm" onClick={() => navigate('/admin/dashboard')}>
            ← Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-6">
        <Card className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full gradient-instagram flex items-center justify-center">
              <ChefHat className="w-8 h-8 text-primary-foreground" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Amministratore</h2>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="w-4 h-4" />
                <span>{user?.email || 'admin@sapori1989.it'}</span>
              </div>
            </div>
          </div>

          <Separator className="my-6" />

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-instagram-purple" />
              <h3 className="text-lg font-semibold text-foreground">Cambia Password</h3>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {resetSent ? (
              <Alert className="bg-success/10 border-success/30">
                <CheckCircle className="w-4 h-4 text-success" />
                <AlertDescription>
                  Email inviata a <strong>{user?.email}</strong>.
                  Controlla la tua casella di posta e segui le istruzioni.
                </AlertDescription>
              </Alert>
            ) : !showPasswordForm ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Per cambiare la password puoi richiedere un link di reset via email
                  oppure inserire direttamente una nuova password.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button variant="outline" onClick={handleRequestReset} disabled={isRequestingReset}>
                    {isRequestingReset ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    Invia email reset
                  </Button>

                  <Button
                    className="gradient-instagram text-primary-foreground"
                    onClick={() => setShowPasswordForm(true)}
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Cambia direttamente
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nuova password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Conferma password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setNewPassword('');
                      setConfirmPassword('');
                      setError('');
                    }}
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    className="gradient-instagram text-primary-foreground"
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Salva password
                  </Button>
                </div>
              </form>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-instagram-purple" />
              <h3 className="text-lg font-semibold text-foreground">Numero WhatsApp locale</h3>
            </div>

            <p className="text-sm text-muted-foreground">
              Inserisci il numero WhatsApp che il ristorante vuole usare per ricevere gli ordini.
              Usa il formato internazionale, ad esempio <strong>393331234567</strong>.
            </p>

            {whatsappError && (
              <Alert variant="destructive">
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>{whatsappError}</AlertDescription>
              </Alert>
            )}

            {whatsappSaved && (
              <Alert className="bg-success/10 border-success/30">
                <CheckCircle className="w-4 h-4 text-success" />
                <AlertDescription>
                  Numero WhatsApp locale aggiornato correttamente.
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSaveWhatsappNumber} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="restaurantWhatsapp">Numero WhatsApp locale</Label>
                <Input
                  id="restaurantWhatsapp"
                  type="tel"
                  placeholder="393331234567"
                  value={restaurantWhatsapp}
                  onChange={(e) => {
                    setRestaurantWhatsapp(e.target.value);
                    setWhatsappSaved(false);
                    setWhatsappError('');
                  }}
                  disabled={isLoadingWhatsapp || isSavingWhatsapp}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  className="gradient-instagram text-primary-foreground"
                  disabled={isLoadingWhatsapp || isSavingWhatsapp}
                >
                  {isLoadingWhatsapp || isSavingWhatsapp ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {isLoadingWhatsapp ? 'Caricamento...' : 'Salvataggio...'}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Salva numero
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground">Esci dall'account</h3>
              <p className="text-sm text-muted-foreground">
                Disconnettiti dall'area amministrazione
              </p>
            </div>
            <Button variant="destructive" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </Card>

        <div className="text-center">
          <a
            href="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Torna al menù pubblico
          </a>
        </div>
      </main>
    </div>
  );
};